import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { FloatingNav, OverlaySideNav, HeroSection as LayoutHeroSection, NavSection } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import {
  Scale,
  Target,
  Users,
  Ban,
  ChevronRight,
  Calculator,
  Calendar,
  Shield,
  AlertTriangle,
  Percent,
  ClipboardList,
  CheckCircle,
  XCircle,
  Eye,
  MessageSquare,
  BarChart3,
  Briefcase,
  UserCheck,
  ArrowRight,
  HelpCircle,
  Info,
} from 'lucide-react';

// ============================================================================
// Navigation Sections
// ============================================================================

const navSections: NavSection[] = [
  { id: 'your-role', label: 'Your Role', icon: <Users className="w-4 h-4" /> },
  { id: 'why-program-exists', label: 'Why It Exists', icon: <Target className="w-4 h-4" /> },
  { id: 'program-design', label: 'Program Design', icon: <Scale className="w-4 h-4" /> },
  { id: 'six-principles', label: 'Six Principles', icon: <ClipboardList className="w-4 h-4" /> },
  { id: 'leader-roles', label: 'Leader Roles', icon: <Briefcase className="w-4 h-4" /> },
  { id: 'no-gates', label: 'Independent Measures', icon: <Ban className="w-4 h-4" /> },
  { id: 'sixty-forty', label: '60/40 Structure', icon: <Percent className="w-4 h-4" /> },
  { id: 'consistency-floor', label: 'Consistency Value', icon: <Shield className="w-4 h-4" /> },
  { id: 'attribution', label: 'Attribution', icon: <Target className="w-4 h-4" /> },
  { id: 'data-accuracy', label: 'Data Accuracy', icon: <AlertTriangle className="w-4 h-4" /> },
  { id: 'single-measure-risk', label: 'Single Measure Risk', icon: <AlertTriangle className="w-4 h-4" /> },
  { id: 'talking-about-data', label: 'Talking Data', icon: <MessageSquare className="w-4 h-4" /> },
  { id: 'what-to-avoid', label: 'What to Avoid', icon: <Ban className="w-4 h-4" /> },
  { id: 'dashboards', label: 'Dashboards', icon: <BarChart3 className="w-4 h-4" /> },
  { id: 'communication-cadence', label: 'Communication', icon: <Calendar className="w-4 h-4" /> },
  { id: 'success', label: 'Success', icon: <CheckCircle className="w-4 h-4" /> },
  { id: 'next-steps', label: 'Next Steps', icon: <ChevronRight className="w-4 h-4" /> },
];

// ============================================================================
// Animation Variants
// ============================================================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// ============================================================================
// Section Wrapper Component
// ============================================================================

function Section({
  id,
  children,
  className,
  background = 'white',
}: {
  id: string;
  children: React.ReactNode;
  className?: string;
  background?: 'white' | 'gray';
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section
      id={id}
      ref={ref}
      className={cn(
        'py-12 md:py-16 scroll-mt-20',
        background === 'gray' ? 'bg-gray-50' : 'bg-white',
        className
      )}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={fadeInUp}
        className="max-w-6xl mx-auto px-6"
      >
        {children}
      </motion.div>
    </section>
  );
}

// ============================================================================
// Talking Script Component
// ============================================================================

function TalkingScript({
  prompt,
  script,
  variant = 'teal',
}: {
  prompt: string;
  script: string;
  variant?: 'teal' | 'navy' | 'warning';
}) {
  const colors = {
    teal: 'bg-eventus-teal/5 border-l-eventus-teal',
    navy: 'bg-eventus-navy/5 border-l-eventus-navy',
    warning: 'bg-amber-50 border-l-amber-500',
  };

  return (
    <div className={cn('p-4 border-l-4 rounded-r-lg', colors[variant])}>
      <p className="text-base text-gray-500 mb-2 font-medium">{prompt}</p>
      <p className="text-gray-700 italic leading-relaxed">"{script}"</p>
    </div>
  );
}

// ============================================================================
// Hero Section
// ============================================================================

function HeroSection() {
  return (
    <LayoutHeroSection
      role="non-physician"
      badgeText="APC & Teletriage Leadership"
      title="Leader Support Guide"
      description="Preparing for conversations with your team about EQuIP 2026"
      subtitle="ADCOEs, Director of Teletriage, and RDCOEs—those closest to day-to-day APC performance and are responsible for consistent messaging across regions."
    />
  );
}

// ============================================================================
// Your Role Section
// ============================================================================

function YourRoleSection() {
  return (
    <Section id="your-role">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Your Role as a Leader
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        How you share this program with your team shapes how they will receive this program. We want to make sure you're prepared to lead with clarity.
      </p>

      <div className="space-y-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <p className="text-gray-700 leading-relaxed">
              You don't need to memorize every detail. Understanding the key principles to explain them in your own words is crucial.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-eventus-teal/5 border-eventus-teal/20">
          <CardContent className="p-4">
            <h4 className="font-semibold text-eventus-navy mb-2">Your job IS to:</h4>
            <ul className="space-y-2 text-base text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                Explain the program clearly
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                Support clinicians in understanding how they can meet standards
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                Use data to guide conversations
              </li>
            </ul>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-eventus-navy mb-2">Your job is NOT to:</h4>
            <ul className="space-y-2 text-base text-gray-700">
              <li className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-error mt-0.5 flex-shrink-0" />
                Determine exceptions
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-error mt-0.5 flex-shrink-0" />
                Negotiate thresholds
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Section>
  );
}

// ============================================================================
// Why This Program Exists Section
// ============================================================================

function WhyProgramExistsSection() {
  return (
    <Section id="why-program-exists" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Why This Program Exists
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        EQuIP funding is made possible because Eventus achieves its goals. When we succeed as a company, the incentive pool grows. EQuIP rewards clinicians whose work directly contributes to this success.
      </p>

      <Card className="mb-6">
        <CardContent className="p-6">
          <h4 className="font-semibold text-eventus-navy mb-4">Eventus Goals That Fund EQuIP:</h4>
          <ul className="space-y-3">
            {[
              'Grow our business — serve more patients in more places',
              'Improve outcomes for our patients',
              'Ensure consistency in the care we provide',
              'Operate as one engaged Eventus',
              'Build scalable infrastructure for sustained growth'
            ].map((goal, index) => (
              <li key={index} className="flex items-start gap-3">
                <Target className="w-5 h-5 text-eventus-teal mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{goal}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <TalkingScript
        prompt="When your team asks 'Where does the incentive funding come from?'"
        script="It comes from Eventus succeeding as a company. When we hit our quality targets, grow sustainably, and deliver consistent care—that's what funds the incentive pool. EQuIP is how we share that success with the clinicians who make it happen."
      />
    </Section>
  );
}

// ============================================================================
// How the Program Was Designed Section
// ============================================================================

function ProgramDesignSection() {
  return (
    <Section id="program-design">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        How the Program Was Designed
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        We intentionally designed EQuIP to be fair, transparent, and focused on what clinicians can actually control.
      </p>

      <div className="space-y-4 mb-8">
        {[
          {
            icon: UserCheck,
            title: 'Focus on What Clinicians Control',
            description: 'Not team averages or outcomes they can\'t influence. Your panel, your patients, your results.'
          },
          {
            icon: Scale,
            title: 'Apply Consistently Across Roles',
            description: 'Same structure, same thresholds, same rules—APCs, Teletriage, all employment levels.'
          },
          {
            icon: Eye,
            title: 'Use Transparent Measurement',
            description: 'No hidden formulas or subjective judgment. The math is simple and the rules are clear.'
          }
        ].map((item) => (
          <Card key={item.title}>
            <CardContent className="p-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-eventus-teal/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-eventus-teal" />
              </div>
              <div>
                <h4 className="font-semibold text-eventus-navy mb-1">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-eventus-navy text-white mb-6">
        <CardContent className="p-6">
          <h4 className="font-semibold mb-4">Program Intent</h4>
          <p className="text-white/90 mb-4">The EQuiP program is designed to be:</p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-eventus-teal" />
              <span>Fair and transparent</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-eventus-teal" />
              <span>Based on metrics that can be <strong>reliably measured</strong>, not inferred</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-eventus-teal" />
              <span>Fully aligned with Outcomes-Centered Healthcare priorities</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      <TalkingScript
        prompt="When your team asks 'Why is it set up this way?'"
        script="The program is designed to measure what you can actually control. You're measured on patients at your facilities. Everyone uses the same 60/40 model and 90% threshold."
      />
    </Section>
  );
}

// ============================================================================
// Six Principles Section
// ============================================================================

function SixPrinciplesSection() {
  const principles = [
    {
      id: 1,
      title: 'Gates Are Not Used',
      description: 'Each measure stands on its own. Not meeting one does not eliminate the potential for earning an incentive this quarter.',
      script: 'Each measure counts independently. Meet a measure and earn that portion of the incentive.'
    },
    {
      id: 2,
      title: 'Universal Structure',
      description: 'The 60/40 model and 90% threshold apply to everyone—APCs, Teletriage, all employment levels.',
      script: 'Same rules for everyone. Consistency not just in measures but also in design.'
    },
    {
      id: 3,
      title: 'Equal Division',
      description: 'The 60% outcome portion is always divided equally among active measures.',
      script: 'Each measure carries the same weight. Equal division means consistent structure.'
    },
    {
      id: 4,
      title: 'Fair Scaling',
      description: 'Part-time clinicians have a lower maximum award but are held to the same quality standards.',
      script: 'Lower cap, same standards. It\'s proportional.'
    },
    {
      id: 5,
      title: 'Consistency Is Always Worth Pursuing',
      description: 'Strong Consistency performance means that even if Outcome Measures are not met, your work on documentation, coding accuracy, and timeliness still contributes to your award.',
      script: 'Your consistency work always counts toward your award.'
    },
    {
      id: 6,
      title: 'Patient-Centered',
      description: 'Clinicians are measured on outcomes for their own patients—not team averages.',
      script: 'Your patients, your numbers. Not the team average.'
    }
  ];

  return (
    <Section id="six-principles" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        The Six Principles
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        These six principles answer 80% of the questions your team will ask. When you understand these deeply, you can explain the program in your own words.
      </p>

      <Accordion type="single" collapsible className="space-y-3">
        {principles.map((principle) => (
          <AccordionItem key={principle.id} value={`principle-${principle.id}`} className="border rounded-lg bg-white">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-eventus-teal/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-eventus-teal">{principle.id}</span>
                </div>
                <div className="text-left">
                  <span className="font-semibold text-eventus-navy">{principle.title}</span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="pl-11">
                <p className="text-gray-600 mb-4">{principle.description}</p>
                <div className="p-3 bg-eventus-teal/5 rounded-lg border-l-4 border-eventus-teal">
                  <p className="text-xs text-gray-500 mb-1">What to say:</p>
                  <p className="text-gray-700 italic">"{principle.script}"</p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

// ============================================================================
// Leader Roles Section
// ============================================================================

function LeaderRolesSection() {
  return (
    <Section id="leader-roles">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Leader Roles & Responsibilities
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        ADCOEs, the Director of Teletriage, and RDCOEs play a critical role in supporting APC success by reinforcing the purpose of EQuIP, ensuring expectations are clear, and helping clinicians monitor their performance before quarter-end.
      </p>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-eventus-teal" />
              ADCOEs & Director of Teletriage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-base text-gray-500 mb-4">Closest to day-to-day APC performance</p>
            <ul className="space-y-3">
              {[
                'Reinforce the purpose of EQuIP within an outcomes-centered model',
                'Ensure expectations are clear and consistently supported',
                'Encourage clinicians to review performance data during the quarter to support early insight',
                'Support APCs in understanding trends and areas of focus',
                'Normalize the use of performance data as a planning tool and encourage regular review of dashboard',
                'Maintain consistent language and expectations across all conversations'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-base text-gray-700">
                  <CheckCircle className="w-4 h-4 text-eventus-teal mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Briefcase className="w-5 h-5 text-eventus-navy" />
              RDCOEs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-base text-gray-500 mb-4">Support ADCOEs by:</p>
            <ul className="space-y-3">
              {[
                'Reinforcing consistent messaging across regions',
                'Helping ADCOEs navigate questions or concerns',
                'Ensuring alignment on expectations and thresholds',
                'Redirecting program design or exception questions appropriately to HR'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-base text-gray-700">
                  <CheckCircle className="w-4 h-4 text-eventus-navy mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </Section>
  );
}

// ============================================================================
// No Gates Section
// ============================================================================

function NoGatesSection() {
  return (
    <Section id="no-gates" background="gray">
      <Badge className="bg-eventus-purple/10 text-eventus-purple mb-4">The Big Change</Badge>
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Each Measure Stands Alone
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        <strong>Each measure stands alone.</strong> Performance in one area supports overall success—strong results are recognized independently.
      </p>

      <Card className="border-2 border-eventus-teal mb-6">
        <CardContent className="p-6">
          <h3 className="text-xl font-bold text-eventus-teal mb-4">How It Works</h3>
          <div className="space-y-2">
            {[
              { label: 'Measure 1', passed: true, value: '12%' },
              { label: 'Measure 2', passed: true, value: '12%' },
              { label: 'Measure 3', passed: false, value: '—' },
              { label: 'Measure 4', passed: true, value: '12%' },
              { label: 'Measure 5', passed: true, value: '12%' }
            ].map((measure, index) => (
              <div
                key={index}
                className={cn(
                  'p-2 rounded flex items-center justify-between text-sm',
                  measure.passed ? 'bg-success/10' : 'bg-gray-100'
                )}
              >
                <span className="text-gray-700">{measure.label}</span>
                <div className="flex items-center gap-2">
                  {measure.passed ? (
                    <CheckCircle className="w-4 h-4 text-success" />
                  ) : (
                    <span className="w-4 h-4 text-gray-400">—</span>
                  )}
                  <span className={cn('font-bold', measure.passed ? 'text-success' : 'text-gray-400')}>
                    {measure.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-eventus-teal mt-4 font-medium">Meet 4 of 5 measures = earn 48% of outcome component</p>
        </CardContent>
      </Card>

      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-2 text-gray-700">
          <CheckCircle className="w-5 h-5 text-success" />
          <span>Meet a measure (≥90%) → earn that measure's portion</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <CheckCircle className="w-5 h-5 text-eventus-teal" />
          <span>Each measure is independent—performance on one doesn't affect the others</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <Shield className="w-5 h-5 text-eventus-teal" />
          <span>Strong performance in multiple areas adds up</span>
        </div>
      </div>

      <TalkingScript
        prompt="When your team asks 'What if I don't meet one measure?'"
        script="Each measure counts independently. If you meet four out of five outcome measures, you earn 48% of the outcome component. Each measure stands on its own—strong performance adds up."
      />
    </Section>
  );
}

// ============================================================================
// 60/40 Structure Section
// ============================================================================

function SixtyFortySection() {
  return (
    <Section id="sixty-forty">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        The 60/40 Structure
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        The award is built from two independent parts. They work separately—not meeting an Outcome Measure doesn't impact Consistency earnings. Strong Consistency can offset missed Outcomes for incentive potential.
      </p>

      <Card className="mb-6 overflow-hidden">
        <div className="flex">
          <div className="w-[60%] bg-pink-500 text-white p-6">
            <div className="text-3xl font-bold">60%</div>
            <div className="text-lg font-semibold">OUTCOMES</div>
            <div className="text-sm text-white/80 mt-1">Clinical quality tied to ACO success</div>
          </div>
          <div className="w-[40%] bg-blue-500 text-white p-6">
            <div className="text-3xl font-bold">40%</div>
            <div className="text-lg font-semibold">CONSISTENCY</div>
            <div className="text-sm text-white/80 mt-1">PIMs, Coding, Sig Standards</div>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card className="bg-pink-50 border-pink-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-pink-800 mb-2">Outcome Measures</h4>
            <p className="text-sm text-pink-700">AWV, A1C, BCS, CRCS, PHQ-9, HTN, ACP, ED Send-Out</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-blue-800 mb-2">Consistency Measures</h4>
            <p className="text-sm text-blue-700">PIMs, Coding, Signature Standards</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-gray-50 mb-6">
        <CardContent className="p-4">
          <h4 className="font-semibold text-eventus-navy mb-3">Key Details</h4>
          <ul className="space-y-2 text-base text-gray-700">
            <li className="flex items-start gap-2">
              <Percent className="w-4 h-4 text-eventus-teal mt-0.5" />
              <span>All measures use a 90% threshold</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-warning mt-0.5" />
              <span><strong>Exception:</strong> ED Send-Out must be &lt;25% (lower is better)</span>
            </li>
            <li className="flex items-start gap-2">
              <Target className="w-4 h-4 text-eventus-teal mt-0.5" />
              <span>The 60% is divided equally among active Outcome Measures</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="w-4 h-4 text-gray-400 mt-0.5" />
              <span>Teletriage has 2 Consistency Measures (no PIMs)</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      <TalkingScript
        prompt="How to explain this:"
        script="Think of it as two buckets. The Outcome bucket is worth 60%, the Consistency bucket is worth 40%. They fill separately. Strong Consistency performance earns independently of Outcome results."
      />
    </Section>
  );
}

// ============================================================================
// Consistency Floor Section
// ============================================================================

function ConsistencyFloorSection({ employmentStatus }: { employmentStatus?: import('@/data/types').EmploymentStatus }) {
  const isPartTime = employmentStatus === 'part-time';
  return (
    <Section id="consistency-floor" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Consistency Is Always Worth Pursuing
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        Even if Outcome Measures are not met, strong Consistency performance could result in up to 40% of the maximum award potential. This rewards foundational work—PIMs compliance, coding accuracy, and documentation quality.
      </p>

      <Card className="mb-6">
        <CardContent className="p-6">
          <h4 className="font-semibold text-eventus-navy mb-4">
            Role-Specific Floors {isPartTime ? '(Part-Time, 3 days)' : '(Full-Time)'}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-base text-gray-500">Role</th>
                  <th className="text-center py-2 text-base text-gray-500">Maximum</th>
                  <th className="text-center py-2 text-base text-gray-500">Consistency (40%)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 font-medium">APC</td>
                  <td className="text-center py-3">{isPartTime ? '$1,800' : '$3,000'}</td>
                  <td className="text-center py-3 font-bold text-eventus-teal">{isPartTime ? '$720' : '$1,200'}</td>
                </tr>
                <tr>
                  <td className="py-3 font-medium">Teletriage</td>
                  <td className="text-center py-3">{isPartTime ? '$900' : '$1,500'}</td>
                  <td className="text-center py-3 font-bold text-eventus-teal">{isPartTime ? '$360' : '$600'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200 mb-6">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-blue-800 text-sm">
            <strong>Key point:</strong> Leaders should emphasize that Consistency is always worth pursuing, regardless of Outcome performance. It represents meaningful earning potential when you maintain strong PIMs, coding, and documentation.
          </p>
        </CardContent>
      </Card>

      <TalkingScript
        prompt="When your team asks 'What if I do not meet all my Outcome Measures?'"
        script={isPartTime
          ? "Consistency adds up. If you meet all three Consistency Measures, you have the potential to earn a meaningful portion of the total incentive. It's earned through strong PIMs, coding, and documentation."
          : "Consistency adds up. If you meet all three Consistency Measures, you have the potential to earn $1,200 as a full-time APC. That's a meaningful portion of the total incentive, and it's earned through strong PIMs, coding, and documentation."
        }
      />
    </Section>
  );
}

// ============================================================================
// Attribution Section
// ============================================================================

function AttributionSection() {
  return (
    <Section id="attribution">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Each Clinician Owns Their Patients
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        Clinicians are measured on outcomes for patients in their attributed panel—not a team average, not someone else's patients. This creates clear accountability without shared penalties.
      </p>

      <Card className="mb-6">
        <CardContent className="p-6">
          <h4 className="font-semibold text-eventus-navy mb-4">Attribution Chain</h4>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
            {[
              { label: 'CLINICIAN', sublabel: '(GLR)' },
              { label: 'FACILITY', sublabel: '(GLR)' },
              { label: 'SERVICE', sublabel: '(EHR Flags)' },
              { label: 'PATIENTS', sublabel: '(Panel)' }
            ].map((step, index) => (
              <div key={step.label} className="flex items-center gap-2">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-eventus-teal/10 flex items-center justify-center mx-auto">
                    <Users className="w-6 h-6 text-eventus-teal" />
                  </div>
                  <p className="mt-2 text-xs font-medium text-gray-700">{step.label}</p>
                  <p className="text-xs text-gray-500">{step.sublabel}</p>
                </div>
                {index < 3 && <ArrowRight className="w-4 h-4 text-gray-400" />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2 mb-6">
        <p className="font-medium text-gray-700">This means:</p>
        <ul className="space-y-2 text-gray-600">
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-success" />
            Their results reflect their care
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-success" />
            They are not penalized for others' performance
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-success" />
            They are not credited for others' performance
          </li>
        </ul>
      </div>

      <TalkingScript
        prompt="When your team asks 'Why is this patient in my numbers?'"
        script="Attribution comes from two places: your facility assignments in the GLR, and service flags on patients in the EHR. If something looks off, we check those two sources first. You're only measured on patients attributed to you."
      />
    </Section>
  );
}

// ============================================================================
// Data Accuracy Section
// ============================================================================

function DataAccuracySection() {
  return (
    <Section id="data-accuracy" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Data Accuracy Is Everyone's Job
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        If the underlying data is not accurate, the measurements will be inaccurate. Attribution depends on the GLR being accurate and up-to-date.
      </p>

      <Card className="mb-6 border-2 border-warning/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-warning" />
            <h4 className="font-semibold text-eventus-navy">Data Accuracy Is a Shared Responsibility</h4>
          </div>

          <div className="space-y-6">
            <div>
              <h5 className="font-medium text-gray-700 mb-2">LEADERS (ADCOEs, Dir. of Teletriage, RDCOEs) should:</h5>
              <ul className="space-y-1 text-base text-gray-600">
                <li>• Verify GLR assignments are current for their teams</li>
                <li>• Flag coverage situations that may skew attribution</li>
                <li>• Escalate data issues early—not after quarter close</li>
              </ul>
            </div>

            <div>
              <h5 className="font-medium text-gray-700 mb-2">CLINICIANS should:</h5>
              <ul className="space-y-1 text-base text-gray-600">
                <li>• Review their attributed patient lists periodically</li>
                <li>• Report patients who shouldn't be in their panel</li>
              </ul>
            </div>

            <div>
              <h5 className="font-medium text-gray-700 mb-2">SUPPORT TEAMS should:</h5>
              <ul className="space-y-1 text-base text-gray-600">
                <li>• Keep EHR service flags accurate and up to date</li>
                <li>• Process GLR changes promptly</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-eventus-purple/5 border-eventus-purple/20 mb-6">
        <CardContent className="p-4">
          <h4 className="font-semibold text-eventus-navy mb-2">A Shared Responsibility</h4>
          <p className="text-base text-gray-700 mb-2">
            Incentives are made possible by accurately capturing the care we provide. This includes accurate use of G2211 when clinically appropriate.
          </p>
          <p className="text-sm text-eventus-teal font-medium">
            The full care team plays a role in ensuring care is both delivered and documented accurately.
          </p>
        </CardContent>
      </Card>

      <TalkingScript
        prompt="When your team asks about data issues:"
        script="Attribution comes from the GLR and EHR flags. If something looks off, we check those two sources first. Let me know early if you see patients who shouldn't be there—we can investigate before quarter close, not after."
      />
    </Section>
  );
}

// ============================================================================
// Specialty Measure Focus Section
// ============================================================================

function SingleMeasureRiskSection() {
  return (
    <Section id="single-measure-risk">
      <Badge className="bg-blue-100 text-blue-700 mb-4">Specialty Focus</Badge>
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Specialty Roles Have Focused Measures
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        Some specialty roles have a single, focused Outcome Measure that aligns with their clinical work. Here's how the incentive opportunity breaks down.
      </p>

      <Card className="mb-6 border-2 border-blue-200">
        <CardContent className="p-6">
          <h4 className="font-semibold text-eventus-navy mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-600" />
            Specialty Measure Overview
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-gray-500">Role</th>
                  <th className="text-left py-2 text-gray-500">Outcome Measure</th>
                  <th className="text-left py-2 text-gray-500">Outcome Opportunity</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 font-medium">Primary Care APC (Q1 only)</td>
                  <td className="py-2">AWV</td>
                  <td className="py-2 text-eventus-teal font-medium">$1,800 (60%)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Psychiatry APC</td>
                  <td className="py-2">PHQ-9</td>
                  <td className="py-2 text-eventus-teal font-medium">$1,800 (60%)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Palliative Care APC</td>
                  <td className="py-2">ACP</td>
                  <td className="py-2 text-eventus-teal font-medium">$1,800 (60%)</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium">Teletriage</td>
                  <td className="py-2"></td>
                  <td className="py-2 text-eventus-teal font-medium">$900 (60%)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-eventus-teal/5 border-eventus-teal/20 mb-6">
        <CardContent className="p-4">
          <h4 className="font-semibold text-eventus-teal mb-2">Consistency Measures Add Up</h4>
          <p className="text-gray-700 text-sm">
            In addition to the outcome measure, strong performance on PIMs, coding, and documentation adds another 40% earning opportunity.
          </p>
        </CardContent>
      </Card>

      <TalkingScript
        prompt="How to explain this to your team:"
        script="Your outcome measure represents 60% of the incentive opportunity. Consistency measures—PIMs, coding, and documentation—represent another 40%. Both areas contribute to your total earning potential."
      />
    </Section>
  );
}

// ============================================================================
// Talking About Data Section
// ============================================================================

function TalkingAboutDataSection() {
  return (
    <Section id="talking-about-data" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Frame Data as Insight, Not Judgment
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        Performance data is a tool for improvement—not a scorecard for blame. How you present numbers matters. Lead with curiosity and focus on what's actionable going forward.
      </p>

      <div className="space-y-4 mb-6">
        {[
          {
            dont: "You failed A1C this quarter.",
            do: "A1C is trending below 90%. Let's look at what might help next quarter."
          },
          {
            dont: "Your numbers are worse than the other APCs.",
            do: "I see a gap here. What's your read on it?"
          },
          {
            dont: "You need to hit 90% or you won't get paid.",
            do: "The threshold is 90%. Here's where you stand and what we can focus on."
          }
        ].map((pair, index) => (
          <div key={index} className="grid md:grid-cols-2 gap-4">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-error" />
                  <span className="font-semibold text-error text-sm">DON'T</span>
                </div>
                <p className="text-gray-700 text-sm italic">"{pair.dont}"</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span className="font-semibold text-success text-sm">DO</span>
                </div>
                <p className="text-gray-700 text-sm italic">"{pair.do}"</p>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      <Card className="bg-gray-100">
        <CardContent className="p-4">
          <p className="text-gray-700 font-medium mb-2">The goal:</p>
          <ul className="space-y-1 text-base text-gray-600">
            <li>• Shared awareness throughout the quarter</li>
            <li>• No surprises at quarter close</li>
            <li>• Data as a planning tool, not a verdict</li>
          </ul>
        </CardContent>
      </Card>
    </Section>
  );
}

// ============================================================================
// What to Avoid Section
// ============================================================================

function WhatToAvoidSection() {
  return (
    <Section id="what-to-avoid">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        What to Avoid
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {[
          { title: 'DEBATE THRESHOLDS', description: 'The 90% line is consistent and nonnegotiable.' },
          { title: 'PROMISE EXCEPTIONS', description: 'Leaders cannot make individual exceptions.' },
          { title: 'REINTERPRET AFTER CLOSE', description: 'Once the quarter is closed, the results are final.' }
        ].map((item) => (
          <Card key={item.title} className="bg-red-50 border-red-200">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Ban className="w-6 h-6 text-error" />
              </div>
              <h3 className="font-bold text-error mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h4 className="font-semibold text-eventus-navy mb-2">Where to redirect:</h4>
          <p className="text-gray-700 text-sm">
            Questions about program design, thresholds, or exceptions should be directed to <strong>HR</strong>.
          </p>
          <p className="text-gray-500 text-sm mt-2 italic">
            Your role is to explain the program clearly—your responsibility is not to defend or modify it in the moment.
          </p>
        </CardContent>
      </Card>
    </Section>
  );
}

// ============================================================================
// Dashboards Section
// ============================================================================

function DashboardsSection() {
  return (
    <Section id="dashboards" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Dashboards Prevent Surprises
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        Dashboards aren't for quarter-end judgment—they're for mid-quarter awareness. If a clinician is surprised by their results at quarter close, the communication loop needs to be reassessed.
      </p>

      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-eventus-teal" />
            <h4 className="font-semibold text-eventus-navy">Leaders should:</h4>
          </div>
          <ul className="space-y-3">
            {[
              { icon: Eye, text: 'Encourage clinicians to review dashboards regularly' },
              { icon: MessageSquare, text: 'Use data to guide proactive conversations' },
              { icon: BarChart3, text: 'Focus on trends rather than isolated data points' },
              { icon: Target, text: 'Normalize the concept of performance data as a planning tool' }
            ].map((item, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-eventus-teal/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-4 h-4 text-eventus-teal" />
                </div>
                <span className="text-gray-700">{item.text}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </Section>
  );
}

// ============================================================================
// Communication Cadence Section
// ============================================================================

function CommunicationCadenceSection() {
  return (
    <Section id="communication-cadence">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Communication Has a Cadence
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        Consistent communication is essential to maintaining trust. There's a rhythm to the quarter: expectations at the start, check-ins during, and results at the end.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {[
          {
            label: 'QUARTER START',
            title: 'Program Refresh',
            items: ['Clear communication ahead of each quarter', 'Any changes announced']
          },
          {
            label: 'MID-QUARTER',
            title: 'Check Dashboards',
            items: ['Timely updates if measures change', 'Proactive conversations']
          },
          {
            label: 'QUARTER END',
            title: 'Results Shared',
            items: ['Alignment on when results will be shared', 'Payment timing communicated', 'No surprises']
          }
        ].map((phase) => (
          <Card key={phase.label}>
            <CardContent className="p-4 text-center">
              <div className="w-3 h-3 rounded-full bg-eventus-teal mx-auto mb-2" />
              <p className="text-xs font-bold text-gray-500 mb-2">{phase.label}</p>
              <div className="flex items-center justify-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-eventus-teal" />
                <h4 className="font-semibold text-gray-700">{phase.title}</h4>
              </div>
              <ul className="text-xs text-gray-600 space-y-1">
                {phase.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <p className="text-gray-700 text-sm">
            When this cadence is followed, there are no surprises. These communications will be made to APCs from HR. Your role is to support consuming the communication and offering clarity when questions arise.
          </p>
        </CardContent>
      </Card>
    </Section>
  );
}

// ============================================================================
// Success Section
// ============================================================================

function SuccessSection() {
  return (
    <Section id="success" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        What Success Looks Like
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        From a leadership perspective, success in EQuIP looks like:
      </p>

      <Card className="mb-6">
        <CardContent className="p-6">
          <ul className="space-y-4">
            {[
              'Clinicians understand how measures connect to outcomes-centered care',
              'Clinician leaders can articulate the six key principles',
              'No surprises at quarter close',
              'Consistent language and expectations across leaders',
              'Data issues flagged early, not after results are final',
              'Single-measure roles addressed proactively'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-r from-eventus-teal/10 to-eventus-navy/10">
        <CardContent className="p-8 text-center">
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Clinicians need you to support them in understanding the EQuIP program and in how they can meet the standards.
          </p>
          <p className="text-xl font-bold text-eventus-teal">
            Lead with clarity. Answer with confidence.
          </p>
        </CardContent>
      </Card>
    </Section>
  );
}

// ============================================================================
// Next Steps Section
// ============================================================================

function NextStepsSection() {
  return (
    <Section id="next-steps">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Continue Your Preparation
      </h2>

      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/non-physician/framework">
          <Card className="h-full border-2 border-eventus-teal/20 hover:border-eventus-teal/50 hover:shadow-lg transition-all cursor-pointer group">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-eventus-teal/10 flex items-center justify-center mb-4 group-hover:bg-eventus-teal transition-colors">
                <Calculator className="w-6 h-6 text-eventus-teal group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-eventus-navy mb-2">
                Framework Reference
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Detailed reference with interactive calculator and scenarios.
              </p>
              <div className="flex items-center text-eventus-teal font-semibold text-sm">
                View Framework
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/non-physician/faq">
          <Card className="h-full border-2 border-eventus-purple/20 hover:border-eventus-purple/50 hover:shadow-lg transition-all cursor-pointer group">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-eventus-purple/10 flex items-center justify-center mb-4 group-hover:bg-eventus-purple transition-colors">
                <HelpCircle className="w-6 h-6 text-eventus-purple group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-eventus-navy mb-2">FAQ</h3>
              <p className="text-gray-600 text-sm mb-3">
                Frameworks to help you answer the questions your team may ask.
              </p>
              <div className="flex items-center text-eventus-purple font-semibold text-sm">
                View FAQ
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </Section>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface NonPhysicianLeaderGuideProps {
  employmentStatus?: import('@/data/types').EmploymentStatus;
}

export function NonPhysicianLeaderGuide({ employmentStatus }: NonPhysicianLeaderGuideProps = {}) {
  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
      {/* Floating Navigation */}
      <FloatingNav items={[
        { id: 'framework', label: 'Framework', path: '/non-physician/framework' },
        { id: 'leader-guide', label: 'Leader Guide', path: '/non-physician/leader-guide' },
        { id: 'faq', label: 'FAQ', path: '/non-physician/faq' },
        { id: 'leadership', label: 'Leadership Dashboard', path: '/leadership' },
      ]} />

      {/* Overlay Side Navigation */}
      <OverlaySideNav sections={navSections} />

      {/* Hero Section */}
      <HeroSection />

      {/* Sections */}
      <YourRoleSection />
      <WhyProgramExistsSection />
      <ProgramDesignSection />
      <SixPrinciplesSection />
      <LeaderRolesSection />
      <NoGatesSection />
      <SixtyFortySection />
      <ConsistencyFloorSection employmentStatus={employmentStatus} />
      <AttributionSection />
      <DataAccuracySection />
      <SingleMeasureRiskSection />
      <TalkingAboutDataSection />
      <WhatToAvoidSection />
      <DashboardsSection />
      <CommunicationCadenceSection />
      <SuccessSection />
      <NextStepsSection />
    </div>
  );
}
